/* eslint-disable import/order */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Purchases, PurchasesError, ErrorCode } from '@revenuecat/purchases-js';

import { ROUTES } from 'utils/constants';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { getVariant } from 'utils/experiments/useExperiment';
import { KEYS, IDENTIFIERS } from '../utils/constants';
import { useGetParams } from '../utils/useGetParams';
import { handleTrackEvent } from '../utils/handleTrackEvent';
import { usePaywallSkip } from '../utils/usePaywallExit';

import SolidButton from 'components/SolidButton';
import OfferingCard from '../OfferingCard';
import Warranty from '../Warranty';
import Lottie from 'lottie-react';
import heartLoader from 'assets/heart-loader.json';

const ANNUAL_IDENTIFIER = '$rc_annual';

const Offerings = ({
  offerings,
  selectedOffer,
  setSelectedOffer,
  isDiscount,
  identifier,
  queryEmail,
  showSinglePayment,
  isFetchingOfferings,
  showSkipLink,
}) => {
  const { t } = useTranslation();
  const { push, entry } = useGetParams();
  const [email] = useLocalStorage(KEYS.USER_EMAIL);
  const handleSkip = usePaywallSkip('skip_link');

  const entryPoint = entry || 'link';

  // RevenueCat does not guarantee availablePackages order — annual must render first.
  const sortedOfferings = [...offerings].sort((a, b) => {
    if (a.identifier === ANNUAL_IDENTIFIER) return -1;
    if (b.identifier === ANNUAL_IDENTIFIER) return 1;
    return 0;
  });

  // null when no package is in play, so the funnel's plan split is not biased toward 'monthly'.
  const planOf = (pkg) => {
    if (!pkg) return null;
    return pkg.identifier === ANNUAL_IDENTIFIER ? 'annual' : 'monthly';
  };

  const baseTrackProps = (pkg) => ({
    plan: planOf(pkg),
    entry_point: entryPoint,
    paywall_variant: getVariant('paywall'),
  });

  // purchase() charges the default subscription option, and getOfferings only returns eligible trials.
  const hasFreeTrial = (pkg) => {
    const option = pkg && pkg.rcBillingProduct && pkg.rcBillingProduct.defaultSubscriptionOption;
    return Boolean(option && option.trial);
  };

  // The 7-day wording is wrong for free-month and discount links, so they get no trust line.
  const isFreeMonth = identifier === IDENTIFIERS.FREE_MONTH;
  const showTrustLine =
    showSinglePayment && Boolean(selectedOffer) && !isDiscount && !isFreeMonth && hasFreeTrial(selectedOffer);

  // Re-tapping the already selected card is not a plan change, so it must not emit a second event.
  const handleSelectOffer = (offer) => {
    if (!selectedOffer || offer.identifier !== selectedOffer.identifier) {
      handleTrackEvent('Paywall plan selected', baseTrackProps(offer));
    }
    setSelectedOffer(offer);
  };

  const purchasePackage = async (pkg, userEmail) => {
    handleTrackEvent('Payment intent', baseTrackProps(pkg));
    try {
      const purchaseResult = await Purchases.getSharedInstance().purchase({ rcPackage: pkg, customerEmail: userEmail });

      if (Object.keys(purchaseResult.customerInfo.entitlements.active).includes('Premium')) {
        if (hasFreeTrial(pkg)) {
          handleTrackEvent('Trial started', baseTrackProps(pkg));
        }
        handleTrackEvent('Subscription success', baseTrackProps(pkg));
        push(ROUTES.EVIE_SET_PHONE);
      }
    } catch (error) {
      const isRcError = error instanceof PurchasesError;
      handleTrackEvent('Purchase failed', {
        ...baseTrackProps(pkg),
        error_code: isRcError ? ErrorCode[error.errorCode] : 'UnknownError',
        error_message: error?.message,
      });
      if (isRcError && error.errorCode === ErrorCode.UserCancelledError) {
        console.error('purchased cancelled: ', error);
      } else {
        console.error('error', error);
      }
    }
  };

  return (
    <>
      {isFetchingOfferings ? (
        <div className="onboarding-layout__lottie">
          <Lottie animationData={heartLoader} loop style={{ width: 140, height: 110 }} />
        </div>
      ) : (
        <section className={showSinglePayment ? 'single-payment__offering' : 'plan-screen__offerings'}>
          {sortedOfferings.map((offer) => (
            <OfferingCard
              key={offer.identifier}
              offer={offer}
              setSelectedOffer={handleSelectOffer}
              selectedOffer={selectedOffer}
              isDiscount={isDiscount}
              identifier={identifier}
              showSinglePayment={showSinglePayment}
              isFetchingOfferings={isFetchingOfferings}
            />
          ))}
          <SolidButton
            className={showSinglePayment ? 'evie-survey__single-payment-button' : 'evie-survey__payment-button'}
            onClick={() => purchasePackage(selectedOffer, email || queryEmail)}
          >
            {t('evieOnboarding.planScreen.button')}
          </SolidButton>
          {showTrustLine && (
            <p className="single-payment__trustline">{t('evieOnboarding.planScreen.fineprint.reminder')}</p>
          )}
          {showSkipLink && (
            <button type="button" className="single-payment__skiplink" onClick={handleSkip}>
              {t('evieOnboarding.planScreen.skipLink')}
            </button>
          )}
          {!showSinglePayment ? <Warranty /> : ''}
        </section>
      )}
    </>
  );
};

Offerings.defaultProps = {
  selectedOffer: null,
  isDiscount: false,
  identifier: null,
  queryEmail: '',
  showSinglePayment: false,
  isFetchingOfferings: false,
  showSkipLink: false,
};

Offerings.propTypes = {
  offerings: PropTypes.arrayOf(PropTypes.shape({ identifier: PropTypes.string.isRequired })).isRequired,
  selectedOffer: PropTypes.shape({
    identifier: PropTypes.string.isRequired,
    rcBillingProduct: PropTypes.shape({
      currentPrice: PropTypes.shape({
        currency: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }),
  setSelectedOffer: PropTypes.func.isRequired,
  isDiscount: PropTypes.bool,
  identifier: PropTypes.string,
  queryEmail: PropTypes.string,
  showSinglePayment: PropTypes.bool,
  isFetchingOfferings: PropTypes.bool,
  showSkipLink: PropTypes.bool,
};

export default Offerings;
